/**
 * rtc-recovery — pure state machine for WebRTC call-connection recovery.
 *
 * Mirrors src/machines/connection/reducer.ts's `(state, event) → { state,
 * effects }` shape (same reasoning: no sockets/timers/pc/React in here, so
 * every transition is exhaustively unit-testable — see rtc-recovery.test.ts).
 * OpenAI's Realtime API has no ICE-restart, so recovering a dropped WebRTC
 * call means placing a brand-new call (a fresh POST /api/rtc/call reusing
 * the SAME live mic track — see rtc-call.ts's startRtcCall). This module
 * only decides WHEN to do that and what to tell the user; useCallMode.ts is
 * the driver that owns the actual timer/pc/fetch side effects the effects
 * list requests.
 *
 * States: IDLE → DEGRADED (grace timer running) → RECALLING → RECOVERED | DROPPED.
 *  - IDLE: steady state, nothing wrong (or a call that's never had a drop).
 *  - DEGRADED: `pc.connectionState` reported 'disconnected' — a GRACE_MS
 *    window is given to self-heal before trying anything drastic (WebRTC's
 *    'disconnected' is often a transient network flap that resolves back to
 *    'connected' on its own, with no re-call needed at all).
 *  - RECALLING: the grace window ran out (or ICE reported 'failed'/'closed'
 *    outright, skipping the grace period) and a fresh call is in flight on
 *    the SAME mic track.
 *  - RECOVERED: the re-call's answer came back (or the grace window healed
 *    on its own) — steady state again, just remembering the recall budget
 *    is spent.
 *  - DROPPED: a re-call failed, or the recall budget (MAX_RECALLS) was
 *    already spent when ANOTHER failure arrived — this call is over; the
 *    driver runs its own stopCall and offers "Call again" (a fresh user
 *    gesture starts a wholly new call, not a further recovery attempt).
 *
 * RECOVERED is treated as "IDLE with the budget already spent" rather than a
 * one-shot terminal state: a fresh 'disconnected' after a successful recall
 * still gets its own DEGRADED grace window (the connection might just be
 * flapping again, recoverable for free) — but if THAT grace expires or a
 * 'failed'/'closed' arrives while the budget is spent, it drops straight to
 * DROPPED instead of attempting a second recall. IDLE and RECOVERED
 * therefore share one transition table (`handleSteady`), parameterized only
 * by `recallCount`.
 *
 * A user HANGUP always resets to IDLE from any status — the driver's OWN
 * generation-guard (see useCallMode.ts's callGenerationRef) is what stops a
 * stale in-flight recall promise from resurrecting a call after this; this
 * reducer only tracks intent, not the async work itself.
 */

// ── Policy constants (single source of truth for recovery policy) ─

/** How long a DEGRADED connection is given to self-heal before a re-call is
 *  attempted. WebRTC's 'disconnected' state often resolves on its own within
 *  a few seconds (a brief network flap), so this is a genuine grace window,
 *  not just a debounce. */
export const GRACE_MS = 5000;

/** Re-calls allowed per call. OpenAI's Realtime API has no ICE-restart, so a
 *  re-call is a brand-new SDP exchange — cheap enough to try once, but
 *  repeated re-calls on a truly dead network just burn the user's time
 *  before the same DROPPED outcome. */
export const MAX_RECALLS = 1;

// ── State ───────────────────────────────────────────────────────────

export type RtcRecoveryStatus =
  | 'idle'
  | 'degraded'
  | 'recalling'
  | 'recovered'
  | 'dropped';

export interface RtcRecoveryState {
  status: RtcRecoveryStatus;
  /** Re-calls spent so far this call (0 or MAX_RECALLS). */
  recallCount: number;
}

export function initialRtcRecoveryState(): RtcRecoveryState {
  return { status: 'idle', recallCount: 0 };
}

function canRecall(recallCount: number): boolean {
  return recallCount < MAX_RECALLS;
}

// ── Events (inputs) ───────────────────────────────────────────────

export type RtcRecoveryEvent =
  /** Driver: pc.onconnectionstatechange fired. */
  | { type: 'CONNECTION_STATE'; state: RTCPeerConnectionState }
  /** Driver: the GRACE_MS timer elapsed with no 'connected' in between. */
  | { type: 'GRACE_EXPIRED' }
  /** Driver: the in-flight re-call's startRtcCall resolved. */
  | { type: 'RECALL_SUCCEEDED' }
  /** Driver: the in-flight re-call's startRtcCall rejected. */
  | { type: 'RECALL_FAILED' }
  /** Driver: the user hung up (stopCall) — abandon recovery unconditionally. */
  | { type: 'HANGUP' };

// ── Effects (outputs the driver executes) ─────────────────────────

export type RtcRecoveryEffect =
  /** Arm the GRACE_MS timer → dispatch GRACE_EXPIRED if it isn't cancelled
   *  first by a 'connected'/failure event. */
  | { type: 'START_GRACE_TIMER' }
  /** Clear a running grace timer (it resolved one way or another already). */
  | { type: 'CANCEL_GRACE_TIMER' }
  /** Place a fresh call on the SAME mic track/stream, then dispatch
   *  RECALL_SUCCEEDED/RECALL_FAILED once it settles. */
  | { type: 'START_RECALL' }
  /** Recovery is exhausted — end the call and offer "Call again". */
  | { type: 'END_CALL' };

export interface RtcRecoveryResult {
  state: RtcRecoveryState;
  effects: RtcRecoveryEffect[];
}

// ── Reducer ───────────────────────────────────────────────────────

/** IDLE and RECOVERED share this table — see the header comment for why. */
function handleSteady(
  state: RtcRecoveryState,
  event: RtcRecoveryEvent
): RtcRecoveryResult {
  if (event.type !== 'CONNECTION_STATE') return { state, effects: [] };
  if (event.state === 'disconnected') {
    return {
      state: { status: 'degraded', recallCount: state.recallCount },
      effects: [{ type: 'START_GRACE_TIMER' }],
    };
  }
  if (event.state === 'failed' || event.state === 'closed') {
    return canRecall(state.recallCount)
      ? {
          state: { status: 'recalling', recallCount: state.recallCount + 1 },
          effects: [{ type: 'START_RECALL' }],
        }
      : {
          state: { status: 'dropped', recallCount: state.recallCount },
          effects: [{ type: 'END_CALL' }],
        };
  }
  // 'connected' (already steady — no-op) / 'connecting' / 'new'.
  return { state, effects: [] };
}

function handleDegraded(
  state: RtcRecoveryState,
  event: RtcRecoveryEvent
): RtcRecoveryResult {
  if (event.type === 'GRACE_EXPIRED') {
    return canRecall(state.recallCount)
      ? {
          state: { status: 'recalling', recallCount: state.recallCount + 1 },
          effects: [{ type: 'START_RECALL' }],
        }
      : {
          state: { status: 'dropped', recallCount: state.recallCount },
          effects: [{ type: 'END_CALL' }],
        };
  }
  if (event.type !== 'CONNECTION_STATE') return { state, effects: [] };
  if (event.state === 'connected') {
    return {
      state: { status: 'recovered', recallCount: state.recallCount },
      effects: [{ type: 'CANCEL_GRACE_TIMER' }],
    };
  }
  if (event.state === 'failed' || event.state === 'closed') {
    return canRecall(state.recallCount)
      ? {
          state: { status: 'recalling', recallCount: state.recallCount + 1 },
          effects: [{ type: 'CANCEL_GRACE_TIMER' }, { type: 'START_RECALL' }],
        }
      : {
          state: { status: 'dropped', recallCount: state.recallCount },
          effects: [{ type: 'CANCEL_GRACE_TIMER' }, { type: 'END_CALL' }],
        };
  }
  // 'disconnected' again (redundant — grace timer already running) /
  // 'connecting' / 'new'.
  return { state, effects: [] };
}

function handleRecalling(
  state: RtcRecoveryState,
  event: RtcRecoveryEvent
): RtcRecoveryResult {
  if (event.type === 'RECALL_SUCCEEDED') {
    return {
      state: { status: 'recovered', recallCount: state.recallCount },
      effects: [],
    };
  }
  if (event.type === 'RECALL_FAILED') {
    return {
      state: { status: 'dropped', recallCount: state.recallCount },
      effects: [{ type: 'END_CALL' }],
    };
  }
  // CONNECTION_STATE / GRACE_EXPIRED while a recall is already in flight are
  // ignored — the driver nulls a superseded pc's onconnectionstatechange
  // before discarding it (see useCallMode.ts's performRtcRecall), so stray
  // events from an old pc shouldn't even reach here; this is defense in
  // depth, not the primary guard.
  return { state, effects: [] };
}

export function rtcRecoveryReducer(
  state: RtcRecoveryState,
  event: RtcRecoveryEvent
): RtcRecoveryResult {
  if (event.type === 'HANGUP') {
    const effects: RtcRecoveryEffect[] =
      state.status === 'degraded' ? [{ type: 'CANCEL_GRACE_TIMER' }] : [];
    return { state: initialRtcRecoveryState(), effects };
  }

  switch (state.status) {
    case 'idle':
    case 'recovered':
      return handleSteady(state, event);
    case 'degraded':
      return handleDegraded(state, event);
    case 'recalling':
      return handleRecalling(state, event);
    case 'dropped':
      // Terminal until HANGUP resets it (a fresh startCall's own driver
      // state has already moved on by then regardless).
      return { state, effects: [] };
  }
}

// ── Presentation projection ───────────────────────────────────────

/** What the CallNotice should say for a given status — a pure function of
 *  `status` alone (never `recallCount`, which is an internal budget detail
 *  with no distinct copy). `'none'` means "nothing to show" — the driver
 *  must leave whatever notice is currently up untouched (e.g. some other,
 *  unrelated topic), not clear it.
 *
 *  `'error'` (DROPPED) intentionally carries no onClick — a pure module has
 *  no callback to wire the "Call again" tap to (that's `startCall`, owned
 *  by the driver). useCallMode.ts's END_CALL effect handler builds and
 *  shows that notice itself instead of going through this projection. */
export type RtcRecoveryPresentation =
  | { kind: 'none' }
  | { kind: 'warning'; title: string; subtitle: string }
  | { kind: 'success'; title: string }
  | { kind: 'error'; title: string; subtitle: string; actionLabel: string };

// Copy consistent with FB28's existing CONNECTION-topic voice ("Connection
// lost — reconnecting…" / "You're back online — go ahead.") — see
// useCallMode.ts's handleConnectionDrop/handleConnectionRestored.
const CONNECTION_HICCUP_TITLE = 'Call connection hiccup — hanging on…';
const CONNECTION_HICCUP_SUBTITLE =
  "Hold on — Sara can't hear you until it's back.";
const RECOVERED_TITLE = "You're back — go ahead.";
const DROPPED_TITLE = "Sara couldn't reconnect.";
const DROPPED_SUBTITLE =
  'The call ended, but nothing from your conversation was lost.';
const DROPPED_ACTION_LABEL = 'Call again';

export function rtcRecoveryPresentation(
  status: RtcRecoveryStatus
): RtcRecoveryPresentation {
  switch (status) {
    case 'idle':
      return { kind: 'none' };
    case 'degraded':
    case 'recalling':
      // Same "hanging on" copy carries through the re-call attempt itself —
      // nothing new to tell the user until it resolves one way or another.
      return {
        kind: 'warning',
        title: CONNECTION_HICCUP_TITLE,
        subtitle: CONNECTION_HICCUP_SUBTITLE,
      };
    case 'recovered':
      return { kind: 'success', title: RECOVERED_TITLE };
    case 'dropped':
      return {
        kind: 'error',
        title: DROPPED_TITLE,
        subtitle: DROPPED_SUBTITLE,
        actionLabel: DROPPED_ACTION_LABEL,
      };
  }
}
